import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { getAgent, getLeadSettings, listModels, saveSettings, testModel } from '../api';
import type { AgentPreview, LeadField, ModelOption, ModelTest } from '../api';
import type { ClientConfig, Session } from '../types';

type Kind = ModelOption['kind'];
type CardProps = { session: Session; agent: AgentPreview; onSaved: () => Promise<void> };

const KINDS: Kind[] = ['us', 'direct', 'global', 'other'];
const KIND_LABEL: Record<Kind, string> = {
  us: 'US cross-region',
  direct: 'Direct (this region)',
  global: 'Global (may run outside the US)',
  other: 'Other',
};

const SECTIONS: { key: string; label: string; group: 'prompt' | 'event' | 'tools' | 'extract'; help?: string }[] = [
  { key: 'identity', label: 'Who it is', group: 'prompt' },
  { key: 'newLead', label: 'New lead', group: 'prompt', help: 'Used the first time a number contacts the shop.' },
  { key: 'returningLead', label: 'Returning lead', group: 'prompt', help: 'Used when the number has contacted the shop before.' },
  { key: 'leadContext', label: 'Lead source line', group: 'prompt' },
  { key: 'known', label: 'Already known', group: 'prompt', help: 'Only used once some details are collected. {known} becomes that list.' },
  { key: 'style', label: 'Style and length', group: 'prompt' },
  { key: 'questions', label: 'Questions wrapper', group: 'prompt', help: '{questions} becomes the "AI asks" items from Info to collect that are still missing.' },
  { key: 'booking', label: 'Booking on', group: 'prompt', help: 'Only used when booking is on and the calendar is connected. {openTimes} becomes real open times from the calendar.' },
  { key: 'noBooking', label: 'Booking off', group: 'prompt', help: 'Only used when booking is off.' },
  { key: 'guardrails', label: 'Guardrails', group: 'prompt' },
  { key: 'handoff', label: 'Hand-off rule', group: 'prompt', help: 'Only used when hand-off is on.' },
  { key: 'compliance', label: '10DLC note to the AI', group: 'prompt', help: 'Only used when 10DLC rules are on.' },
  { key: 'openingEvent', label: 'Opening trigger', group: 'event', help: 'Hidden message that tells the AI to write the first text after a missed call or new lead.' },
  { key: 'bookingTool', label: 'Booking tool description', group: 'tools' },
  { key: 'handoffTool', label: 'Hand-off tool description', group: 'tools' },
  { key: 'afterBooked', label: 'After a booking succeeds', group: 'tools' },
  { key: 'afterConflict', label: 'After a slot is taken', group: 'tools' },
  { key: 'afterBookingError', label: 'After booking fails', group: 'tools' },
  { key: 'afterHandoff', label: 'After the owner is notified', group: 'tools' },
  { key: 'extractor', label: 'Lead details reader', group: 'extract', help: 'A separate quiet AI pass after each homeowner text. {fieldList} becomes your Info to collect list. It must return JSON.' },
];
const GROUPS: [string, string][] = [
  ['prompt', 'System prompt, in the order the AI reads it'],
  ['event', 'Opening trigger'],
  ['tools', 'Tools and what the AI is told after using them'],
  ['extract', 'Lead details reader (runs after each homeowner text)'],
];
const PLACEHOLDERS = '{businessName} {industry} {leadName} {source} {formResponses} {tone} {maxChars} {questions} {known} {duration} {hours} {now} {timezone} {leadKind} {fieldList} {openTimes}';

const TONES: [string, string][] = [
  ['brief, direct, no filler', 'Direct'],
  ['friendly and professional', 'Friendly'],
  ['formal and polite', 'Formal'],
];

const H2: CSSProperties = { fontSize: 18, margin: '0 0 4px' };
const H3: CSSProperties = { fontSize: 14, margin: '18px 0 6px' };
const CARD: CSSProperties = { marginBottom: 28 };
const ROW: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
const CHECK: CSSProperties = { flexDirection: 'row', alignItems: 'center', gap: 6, fontWeight: 400, whiteSpace: 'nowrap' };
const FIELD_ROW: CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
  padding: '8px 0', borderBottom: '1px solid #eee7da',
};
const PRE: CSSProperties = {
  whiteSpace: 'pre-wrap', background: '#fff', border: '1px solid #e3ddd2', borderRadius: 8,
  padding: 12, fontSize: 13, lineHeight: 1.45, maxHeight: 420, overflow: 'auto', margin: 0,
};
const BADGE: CSSProperties = {
  fontSize: 11, marginLeft: 8, padding: '1px 6px', borderRadius: 999, background: '#f3e3d3', color: '#8a4b1c',
};

function useSaver(onSaved: () => Promise<void>) {
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setMsg('');
    setErr('');
    setBusy(true);
    try {
      await fn();
      await onSaved();
      setMsg(ok);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return { msg, err, busy, run };
}

function Status({ s }: { s: { msg: string; err: string } }) {
  return (
    <>
      {s.msg && <p>{s.msg}</p>}
      {s.err && <p className="err">{s.err}</p>}
    </>
  );
}

export function AgentTab({ session }: { session: Session }) {
  const isAdmin = session.groups.includes('admin');
  const [cfg, setCfg] = useState<ClientConfig | null>(null);
  const [agent, setAgent] = useState<AgentPreview | null>(null);
  const [err, setErr] = useState('');

  const load = async () => {
    const d = await getAgent(session);
    setCfg(d.config);
    setAgent(d.agent ?? null);
  };

  useEffect(() => {
    load().catch((e) => setErr(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  if (!cfg) return <div className="empty">{err || 'Loading agent...'}</div>;

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Agent</h1>
          <div className="sub">
            {isAdmin
              ? 'Everything the text robot is told, what it collects, the model it runs on, and its rules. Saves to this shop\u2019s config. No deploy.'
              : 'How the text robot talks and what it collects. Saves to this shop\u2019s config.'}
          </div>
        </div>
      </div>
      <BasicsCard session={session} cfg={cfg} onSaved={load} />
      <FieldsCard session={session} onSaved={load} />
      {isAdmin && agent && (
        <>
          <ModelCard session={session} agent={agent} onSaved={load} />
          <RulesCard session={session} agent={agent} onSaved={load} />
          <InstructionsCard session={session} agent={agent} onSaved={load} />
          <PreviewCard agent={agent} />
        </>
      )}
      {isAdmin && !agent && <p className="err">Admin details didn't load. The API may be on an older deploy.</p>}
    </>
  );
}

function BasicsCard({ session, cfg, onSaved }: { session: Session; cfg: ClientConfig; onSaved: () => Promise<void> }) {
  const [businessName, setBusinessName] = useState(cfg.businessName ?? '');
  const [tone, setTone] = useState(cfg.tone ?? 'friendly and professional');
  const [aiPrompt, setAiPrompt] = useState(cfg.aiPrompt ?? '');
  const s = useSaver(onSaved);
  const tones: [string, string][] = TONES.some(([v]) => v === tone) ? TONES : [[tone, `Custom: ${tone}`], ...TONES];

  return (
    <form
      className="form"
      style={CARD}
      onSubmit={(e) => {
        e.preventDefault();
        void s.run(
          () => saveSettings(session, { businessName: businessName.trim(), tone, aiPrompt: aiPrompt.trim() }),
          'Saved. The next text uses this.',
        );
      }}
    >
      <h2 style={H2}>Basics</h2>
      <label>
        Business name (must match the 10DLC brand)
        <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
      </label>
      <label>
        Tone
        <select value={tone} onChange={(e) => setTone(e.target.value)}>
          {tones.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label>
        Extra instructions
        <textarea rows={3} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Never discuss price. Keep it short." />
      </label>
      <Status s={s} />
      <button className="primary" type="submit" disabled={s.busy}>{s.busy ? 'Saving...' : 'Save basics'}</button>
    </form>
  );
}

function FieldsCard({ session, onSaved }: { session: Session; onSaved: () => Promise<void> }) {
  const [fields, setFields] = useState<LeadField[] | null>(null);
  const [autoQualify, setAutoQualify] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const s = useSaver(onSaved);

  const reload = async () => {
    const r = await getLeadSettings(session);
    setFields(r.fields);
    setAutoQualify(r.autoQualify);
  };
  useEffect(() => {
    reload().catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  if (!fields) {
    return (
      <div className="form" style={CARD}>
        <h2 style={H2}>Info to collect</h2>
        <p className={loadErr ? 'err' : 'sub'}>{loadErr || 'Loading...'}</p>
      </div>
    );
  }

  const set = (i: number, patch: Partial<LeadField>) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= fields.length) return;
    const n = [...fields];
    [n[i], n[j]] = [n[j], n[i]];
    setFields(n);
  };
  const add = () => {
    let k = 'custom1';
    for (let n = 2; fields.some((f) => f.key === k); n++) k = `custom${n}`;
    setFields([...fields, { key: k, label: '', question: '', ask: true, required: false }]);
  };

  return (
    <div className="form" style={CARD}>
      <h2 style={H2}>Info to collect</h2>
      <div className="sub">
        What the text robot gathers from each lead, in order. <b>AI asks</b>: it asks for it, one question at a time.
        Off: it only saves it if the homeowner mentions it. <b>Required</b>: counts toward Qualified. Answers show up
        in each lead's details panel.
      </div>
      {fields.map((f, i) => (
        <div key={f.key} style={FIELD_ROW}>
          <input
            style={{ flex: '1 1 150px' }}
            value={f.label}
            placeholder="Label, e.g. Roof age"
            onChange={(e) => set(i, { label: e.target.value })}
          />
          <input
            style={{ flex: '3 1 260px' }}
            value={f.question}
            placeholder="How the AI asks, e.g. How old is the roof?"
            onChange={(e) => set(i, { question: e.target.value })}
          />
          <label style={CHECK}>
            <input type="checkbox" checked={f.ask} onChange={(e) => set(i, { ask: e.target.checked })} /> AI asks
          </label>
          <label style={CHECK}>
            <input type="checkbox" checked={f.required} onChange={(e) => set(i, { required: e.target.checked })} /> Required
          </label>
          <button type="button" className="ghost" onClick={() => move(i, -1)} disabled={i === 0}>Up</button>
          <button type="button" className="ghost" onClick={() => move(i, 1)} disabled={i === fields.length - 1}>Down</button>
          <button type="button" className="ghost" onClick={() => setFields(fields.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <div style={ROW}>
        {fields.length < 20 && <button type="button" className="ghost" onClick={add}>Add info to collect</button>}
        <button
          type="button"
          className="ghost"
          disabled={s.busy}
          onClick={() => void s.run(async () => { await saveSettings(session, { leadFields: null }); await reload(); }, 'Reset to the built-in list.')}
        >
          Reset to built-in list
        </button>
      </div>
      <label>
        Qualified
        <select value={autoQualify ? 'on' : 'off'} onChange={(e) => setAutoQualify(e.target.value === 'on')}>
          <option value="on">Mark a lead Qualified once every required item is collected</option>
          <option value="off">Never change the status automatically</option>
        </select>
      </label>
      <Status s={s} />
      <button
        className="primary"
        type="button"
        disabled={s.busy}
        onClick={() => void s.run(async () => {
          const clean = fields.map((f) => ({ ...f, label: f.label.trim(), question: f.question.trim() }));
          const bad = clean.find((f) => !f.label || (f.ask && !f.question));
          if (bad) throw new Error(bad.label ? `Add a question for "${bad.label}", or turn off AI asks` : 'Every item needs a label');
          await saveSettings(session, { leadFields: clean, autoQualify });
          await reload();
        }, 'Saved. The next text uses this.')}
      >
        {s.busy ? 'Saving...' : 'Save info to collect'}
      </button>
    </div>
  );
}

function ModelCard({ session, agent, onSaved }: CardProps) {
  const [models, setModels] = useState<ModelOption[]>([]);
  const [fetchedAt, setFetchedAt] = useState('');
  const [listErr, setListErr] = useState('');
  const [listing, setListing] = useState(false);
  const [modelId, setModelId] = useState(agent.modelId);
  const [temp, setTemp] = useState(agent.temperature);
  const [maxChars, setMaxChars] = useState(agent.maxReplyChars);
  const [test, setTest] = useState<ModelTest | null>(null);
  const [testing, setTesting] = useState(false);
  const s = useSaver(onSaved);

  const refresh = async () => {
    setListing(true);
    setListErr('');
    try {
      const r = await listModels(session);
      setModels(r.models);
      setFetchedAt(r.fetchedAt);
    } catch (e) {
      setListErr(e instanceof Error ? e.message : String(e));
    } finally {
      setListing(false);
    }
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const g: Record<Kind, ModelOption[]> = { us: [], direct: [], global: [], other: [] };
    for (const m of models) (g[m.kind] ?? g.other).push(m);
    return g;
  }, [models]);
  const known = models.some((m) => m.id === modelId);
  const changed = modelId !== agent.modelId;

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      setTest(await testModel(session, modelId));
    } catch (e) {
      setTest({ ok: false, modelId, latencyMs: 0, error: e instanceof Error ? e.message : String(e) });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="form" style={CARD}>
      <h2 style={H2}>Model</h2>
      <label>
        AI model
        <select value={modelId} onChange={(e) => { setModelId(e.target.value); setTest(null); }}>
          {!known && <option value={modelId}>{modelId} (current)</option>}
          {KINDS.filter((k) => grouped[k].length).map((k) => (
            <optgroup key={k} label={KIND_LABEL[k]}>
              {grouped[k].map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.id === agent.defaultModelId ? ' (default)' : ''}
                  {m.id === agent.modelId ? ' (in use)' : ''} - {m.id}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <div className="sub" style={ROW}>
        <span>
          {listing
            ? 'Loading models from AWS...'
            : `${models.length} models from AWS${fetchedAt ? `, fetched ${new Date(fetchedAt).toLocaleTimeString()}` : ''}`}
        </span>
        <button type="button" className="ghost" onClick={() => void refresh()} disabled={listing}>Refresh list</button>
      </div>
      {listErr && <p className="err">{listErr}</p>}
      {modelId.startsWith('global.') && <p className="sub">Global models can run in data centers outside the US.</p>}
      <label>
        Creativity: {temp.toFixed(1)}
        <input type="range" min={0} max={1} step={0.1} value={temp} onChange={(e) => setTemp(Number(e.target.value))} />
      </label>
      <div className="sub">Lower is steadier and more predictable. Higher is more varied. Default 0.6.</div>
      <label>
        Max reply length (characters, 60 to 600)
        <input type="number" min={60} max={600} value={maxChars} onChange={(e) => setMaxChars(Number(e.target.value))} />
      </label>
      <div style={ROW}>
        <button type="button" className="ghost" onClick={() => void runTest()} disabled={testing}>
          {testing ? 'Testing...' : 'Test this model'}
        </button>
        <button
          type="button"
          className="primary"
          disabled={s.busy}
          onClick={() => void s.run(
            () => saveSettings(session, { bedrockModelId: modelId, aiTemperature: temp, maxReplyChars: maxChars }),
            'Saved. The next text uses this.',
          )}
        >
          {s.busy ? (changed ? 'Testing, then saving...' : 'Saving...') : 'Save model settings'}
        </button>
      </div>
      {test && (test.ok ? (
        <div>
          <div className="sub">
            Sample first text from {test.modelId} ({(test.latencyMs / 1000).toFixed(1)}s, {test.reply?.length ?? 0} characters):
          </div>
          <pre style={PRE}>{test.reply}</pre>
        </div>
      ) : (
        <p className="err">Test failed for {test.modelId}: {test.error}</p>
      ))}
      <Status s={s} />
      <p className="sub">
        The test uses your saved instructions and a sample missed-call lead. Changing the model runs this test before saving
        and refuses if it fails. The lead details reader uses the same model. Prices vary a lot between models:{' '}
        <a href="https://aws.amazon.com/bedrock/pricing/" target="_blank" rel="noreferrer">Bedrock pricing</a>.
      </p>
    </div>
  );
}

function RulesCard({ session, agent, onSaved }: CardProps) {
  const [compliance, setCompliance] = useState(agent.complianceEnabled);
  const [handoff, setHandoff] = useState(agent.handoffEnabled);
  const [pauseOnHandoff, setPauseOnHandoff] = useState(agent.handoffPausesAi ?? true);
  const s = useSaver(onSaved);

  return (
    <div className="form" style={CARD}>
      <h2 style={H2}>Rules</h2>
      <label>
        10DLC rules
        <select value={compliance ? 'on' : 'off'} onChange={(e) => setCompliance(e.target.value === 'on')}>
          <option value="on">On. First text starts with the business name and ends with "Reply STOP to opt out."</option>
          <option value="off">Off. Send the AI's first text as written.</option>
        </select>
      </label>
      {!compliance && (
        <p className="err">
          Warning: numbers on a 10DLC campaign must name the brand and offer STOP in the first text. Turning this off can
          get texts filtered or the campaign suspended, which stops texting for this number.
        </p>
      )}
      <label>
        Hand-off to a human
        <select value={handoff ? 'on' : 'off'} onChange={(e) => setHandoff(e.target.value === 'on')}>
          <option value="on">On. The AI can flag a lead for you. You get notified and the lead shows "Needs you."</option>
          <option value="off">Off. The AI never hands off.</option>
        </select>
      </label>
      {handoff && (
        <label>
          When the AI hands off
          <select value={pauseOnHandoff ? 'pause' : 'keep'} onChange={(e) => setPauseOnHandoff(e.target.value === 'pause')}>
            <option value="pause">Pause the AI on that lead. You take over.</option>
            <option value="keep">Keep the AI answering until you text the lead.</option>
          </select>
        </label>
      )}
      <p className="sub">Emoji and special-character cleanup always runs. It keeps each text in the cheaper standard encoding.</p>
      <Status s={s} />
      <button
        className="primary"
        type="button"
        disabled={s.busy}
        onClick={() => void s.run(
          () => saveSettings(session, { complianceEnabled: compliance, handoffEnabled: handoff, handoffPausesAi: pauseOnHandoff }),
          'Saved.',
        )}
      >
        {s.busy ? 'Saving...' : 'Save rules'}
      </button>
    </div>
  );
}

function InstructionsCard({ session, agent, onSaved }: CardProps) {
  const [text, setText] = useState<Record<string, string>>({ ...agent.sections });
  const s = useSaver(onSaved);
  const norm = (v?: string) => (v ?? '').trim();
  const isCustom = (k: string) => norm(text[k]) !== '' && norm(text[k]) !== norm(agent.defaults[k]);
  const dirty = SECTIONS.some(({ key }) => norm(text[key]) !== norm(agent.sections[key]));

  const save = async () => {
    const patch: Record<string, string> = {};
    for (const { key } of SECTIONS) {
      const v = norm(text[key]);
      if (v === norm(agent.sections[key])) continue;
      patch[key] = v === norm(agent.defaults[key]) ? '' : v; // back to default = remove the override
    }
    await saveSettings(session, { promptSections: patch });
    setText((t) => {
      const n = { ...t };
      for (const { key } of SECTIONS) if (!norm(n[key])) n[key] = agent.defaults[key];
      return n;
    });
  };

  return (
    <div className="form" style={CARD}>
      <h2 style={H2}>Instructions</h2>
      <div className="sub">
        Every built-in instruction the AI gets. Empty or reset means the default. These placeholders are filled in
        automatically: {PLACEHOLDERS}
      </div>
      {GROUPS.map(([g, title]) => (
        <div key={g}>
          <h3 style={H3}>{title}</h3>
          {SECTIONS.filter((x) => x.group === g).map(({ key, label, help }) => (
            <div key={key} style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
              <label>
                <span>
                  {label}
                  {isCustom(key) && <span style={BADGE}>Customized</span>}
                </span>
                {help && <span className="sub" style={{ fontWeight: 400 }}> {help}</span>}
                <textarea
                  rows={Math.min(8, Math.max(2, Math.ceil(norm(text[key]).length / 90)))}
                  value={text[key] ?? ''}
                  onChange={(e) => setText({ ...text, [key]: e.target.value })}
                />
              </label>
              {isCustom(key) && (
                <div>
                  <button type="button" className="ghost" onClick={() => setText({ ...text, [key]: agent.defaults[key] })}>
                    Reset to default
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
      <Status s={s} />
      <button className="primary" type="button" disabled={s.busy || !dirty} onClick={() => void s.run(save, 'Saved. The next text uses this.')}>
        {s.busy ? 'Saving...' : dirty ? 'Save instructions' : 'No changes'}
      </button>
    </div>
  );
}

function PreviewCard({ agent }: { agent: AgentPreview }) {
  const [which, setWhich] = useState<'new' | 'returning'>('new');
  return (
    <div className="form" style={CARD}>
      <h2 style={H2}>Preview: exactly what the AI gets</h2>
      <div className="sub">Built by the same code the texting worker runs, with a sample lead that has nothing collected yet. Updates after each save.</div>
      <label>
        Lead type
        <select value={which} onChange={(e) => setWhich(e.target.value as 'new' | 'returning')}>
          <option value="new">New lead (missed call)</option>
          <option value="returning">Returning lead named Pat</option>
        </select>
      </label>
      <div className="sub">System prompt</div>
      <pre style={PRE}>{which === 'new' ? agent.newLeadPrompt : agent.returningLeadPrompt}</pre>
      <div className="sub">Opening trigger (sent as the first message after a missed call)</div>
      <pre style={PRE}>{agent.openingEvent}</pre>
      <p className="sub">
        Tools offered: {agent.tools.length ? agent.tools.join(', ') : 'none'}. Model: {agent.modelId}. Creativity:{' '}
        {agent.temperature}. Max reply: {agent.maxReplyChars} characters.
      </p>
    </div>
  );
}
